import styled from "styled-components";
import CardFrame from "./CardFrame";
import { GridContainer, GridItem } from "./HomeStyle";
import AvatarWithIcon from "components/global/AvatarWithIcon";
import userIcon from "assets/icons/userIconWhite.svg";
import { useQuery } from "@tanstack/react-query";
import { getAllComments, getAllConversations } from "api";
import moment from "moment";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";

const Wrapper = styled.div`
  .engagementCard {
    padding: 16px 16px 8px 16px;
    gap: 8px;
    border-radius: 8px;
    border: 1px solid #e2e2ea;
    background: #fff;
    width: 100%;

    .headerText {
      font-size: 14px;
    }

    .itemContainer {
      display: flex;
      flex-direction: column;
      max-width: 100%;

      &:nth-child(1) {
        color: #07070c;
        font-weight: 500;
      }
    }

    .item {
      padding: 24px 0;
      border-bottom: 1px solid #e2e2ea;
      width: 100%;

      .dmText {
        font-size: 14px;
        color: var(--grey_5);
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
      }

      .senderName {
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
      }
    }

    .itemContainer .item:first-child {
      color: #07070c;
      font-weight: 500;

      .dmText {
        color: #07070c;
        font-weight: 500;
      }
    }

    .itemContainer .item:last-child {
      border-bottom: none;
    }
  }
`;

const SkeletonItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 24px 0;
  border-bottom: 1px solid #e2e2ea;
  width: 100%;

  .avatarSkeleton {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #e2e2ea;
    animation: skeleton-loading 1.5s infinite ease-in-out;
  }

  .contentSkeleton {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;

    .line {
      height: 14px;
      background: #e2e2ea;
      border-radius: 4px;
      animation: skeleton-loading 1.5s infinite ease-in-out;
    }

    .line.short {
      width: 30%;
    }

    .line.medium {
      width: 60%;
    }

    .line.long {
      width: 100%;
    }
  }

  @keyframes skeleton-loading {
    0% {
      background-color: #e0e0e0;
    }
    50% {
      background-color: #f0f0f0;
    }
    100% {
      background-color: #e0e0e0;
    }
  }

  @media (max-width: 768px) {
    .avatarSkeleton {
      width: 32px;
      height: 32px;
    }

    .line {
      height: 12px;
    }
  }
`;

const Engagement = ({ brand }) => {
  const history = useHistory();
  const { data: comments, isLoading: commentsLoading } = useQuery({
    queryKey: ["comments"],
    queryFn: () =>
      getAllComments({
        socials: ["facebook", "instagram", "twitter", "linkedin"],
        count: 3,
        start: 0,
      }),
    select: (data) => {
      const commentsData = data?.data?.data?.data?.slice(0, 3) || [];

      return commentsData.map((comment) => ({
        ...comment,
        brandInfo: {
          name:
            brand?.socials?.socials?.find(
              (social) => social?.platform === comment?.platform
            )?.page?.name || "",
          photo:
            brand?.socials?.socials?.find(
              (social) => social.platform === comment.platform
            )?.page?.photo || "",
        },
      }));
    },
  });

  const { data: messages, isLoading: messagesLoading } = useQuery({
    queryKey: ["messages"],
    queryFn: () =>
      getAllConversations(["facebook", "instagram", "twitter", "linkedin"]),
    select: (data) => data?.data?.data?.slice(0, 3) || [],
  });

  return (
    <GridItem bgColor="#fff">
      <Wrapper>
        <CardFrame headerText="Engagement">
          <GridContainer columns="1fr 1fr" gap="24px">
            <CardFrame
              headerText="Recent DMs"
              className="engagementCard"
              actionBtn="See more"
              onClick={() => history.push("/dashboard/direct-messages")}
            >
              <div className="itemContainer fullWidth">
                {messagesLoading ? (
                  <>
                    {Array(3)
                      .fill(0)
                      .map((_, idx) => (
                        <SkeletonItem key={idx}>
                          <div className="avatarSkeleton"></div>
                          <div className="contentSkeleton">
                            <div className="line long"></div>
                            <div className="line short"></div>
                          </div>
                        </SkeletonItem>
                      ))}
                  </>
                ) : (
                  messages?.map((message, idx) => (
                    <div className="item flexRow alignCenter gap8" key={idx}>
                      <AvatarWithIcon
                        avatar={message?.sender_profile_photo ?? userIcon}
                        social={message?.platform}
                        size={4.0}
                        socialIconSize={"24px"}
                      />
                      <div className="fullWidth l3 flexColumn gap8">
                        <div className="fullWidth flexRow alignCenter justifySpaceBetween">
                          <p className="senderName">{message?.sender_name}</p>
                          <p className="colorGrey6 textNoWrap">
                            {moment(message?.updated_at).fromNow(true)}
                          </p>
                        </div>
                        <p className="dmText ">
                          {message?.last_message?.message}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardFrame>
            <CardFrame
              headerText="Recent Comments"
              className="engagementCard"
              actionBtn="See more"
              onClick={() => history.push("/dashboard/comments")}
            >
              <div className="itemContainer fullWidth">
                {commentsLoading ? (
                  <>
                    {Array(3)
                      .fill(0)
                      .map((_, idx) => (
                        <SkeletonItem key={idx}>
                          <div className="avatarSkeleton"></div>
                          <div className="contentSkeleton">
                            <div className="line long"></div>
                            <div className="line short"></div>
                          </div>
                        </SkeletonItem>
                      ))}
                  </>
                ) : (
                  comments?.map((comment, idx) => (
                    <div className="item flexRow alignCenter gap8" key={idx}>
                      <AvatarWithIcon
                        avatar={comment?.brandInfo?.photo ?? userIcon}
                        social={comment?.platform}
                        size={4.0}
                        socialIconSize={"24px"}
                      />
                      <div className="fullWidth l3 flexColumn gap8">
                        <div className="flexRow alignCenter justifySpaceBetween">
                          <p>{comment?.brandInfo?.name}</p>
                          {/* <p className="colorGrey6">
                            {moment(comment?.created_at).fromNow(true)}
                          </p> */}
                        </div>
                        <p className="dmText">{comment?.content}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardFrame>
          </GridContainer>
        </CardFrame>
      </Wrapper>
    </GridItem>
  );
};

const mapStateToProps = (state) => {
  return {
    brand: state.brand,
  };
};

export default connect(mapStateToProps)(Engagement);
